import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import MaxDurationShareButton from 'blocks/DownloadPage/components/MaxDurationShareButton';
import { TikTokButton } from 'components/SocialButton';
import useTikTokAuthenticator from 'hooks/useTikTokAuthenticator';
import {
  hasWatermarkSelector,
  videoDurationSecondsSelector,
} from 'redux/modules/download';
import { hideModal, pushModal } from 'redux/modules/modal';
import {
  getTikTokCreatorInfo,
  tiktokProviderUserIdSelector,
} from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { createChainedFunction } from 'utils/functions';
import useOnDownloadPageAction from '../useOnDownloadPageAction';
import { MAX_VIDEO_DURATION_SEC } from './utils';

const TiktokPostButton: React.FC = () => {
  const [shouldReauthenticate, setShouldReauthenticate] = useState<boolean>(
    false,
  );

  const providerUserId = useSelector(tiktokProviderUserIdSelector);
  const hasWatermark = useSelector(hasWatermarkSelector);
  const videoDurationSec = useSelector(videoDurationSecondsSelector);

  const { onDownloadPageAction } = useOnDownloadPageAction();
  const dispatch = useDispatch<Dispatch>();

  const handleSuccess = useCallback((): void => {
    dispatch(getTikTokCreatorInfo(providerUserId));
  }, [dispatch, providerUserId]);

  const handleError = useCallback(() => {
    setShouldReauthenticate(true);
    dispatch(hideModal());
  }, [dispatch]);

  const { authenticating, withAuthentication } = useTikTokAuthenticator({
    force: shouldReauthenticate,
    onAuthSuccess: handleSuccess,
    onError: handleError,
  });

  const openSocialShareModal = useCallback(() => {
    dispatch(
      pushModal({
        name: 'SocialShare',
        params: {
          platform: 'tiktok',
        },
      }),
    );
  }, [dispatch]);

  return (
    <MaxDurationShareButton
      platform="TikTok"
      videoDurationSec={videoDurationSec}
      maxVideoDurationSec={MAX_VIDEO_DURATION_SEC}
      tooltTipContent={
        hasWatermark
          ? 'TikTok does not accept videos with a watermark'
          : undefined
      }
    >
      <TikTokButton
        size="small"
        params={withAuthentication}
        disabled={authenticating || hasWatermark}
        onClick={createChainedFunction(
          () => onDownloadPageAction('tiktokShare'),
          withAuthentication(openSocialShareModal),
        )}
      />
    </MaxDurationShareButton>
  );
};

export default TiktokPostButton;
